import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, ClipboardList, Building, Shield, ArrowRight, ChevronRight } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();

  const roles = [
    {
      id: 'donor',
      title: 'Donor / Organization',
      description: 'Submit projects for verification, track progress, and access verified reports.',
      icon: Building,
      features: ['Submit verification requests', 'Track project progress', 'Download verified reports'],
      path: '/register/donor',
      color: 'emerald'
    },
    {
      id: 'agent',
      title: 'Field Agent',
      description: 'Conduct field verification, submit reports with evidence, and contribute to M&E.',
      icon: ClipboardList,
      features: ['Receive project assignments', 'Submit verification reports', 'Upload field evidence'],
      path: '/register/agent',
      color: 'blue',
      note: 'Requires admin approval'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-4xl page-enter">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_80a6bcee-7228-4b60-b359-6ff952ed0f3c/artifacts/ufoinc0k_logo1-removebg-preview.png" 
            alt="Verify Logo" 
            className="h-48 w-auto"
          />
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">
            Join Verify
          </h1>
          <p className="text-slate-500">
            Select your role to create an account
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {roles.map((role) => (
            <Card
              key={role.id}
              className="stat-card hover-lift cursor-pointer border-2 border-transparent hover:border-slate-200 transition-all"
              onClick={() => navigate(role.path)}
              data-testid={`register-${role.id}-card`}
            >
              <CardContent className="p-6">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  role.color === 'emerald' ? 'bg-emerald-100' : 'bg-blue-100'
                }`}>
                  <role.icon className={`w-6 h-6 ${
                    role.color === 'emerald' ? 'text-emerald-600' : 'text-blue-600'
                  }`} />
                </div>

                <h3 className="text-xl font-semibold text-slate-900 mb-2 font-['Manrope']">
                  {role.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {role.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {role.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {role.note && (
                  <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">{role.note}</p>
                  </div>
                )}

                <Button className="w-full" variant={role.color === 'emerald' ? 'default' : 'outline'}>
                  Register as {role.title.split('/')[0].trim()}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-slate-900 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-6 p-4 bg-slate-100 rounded-xl text-center">
          <p className="text-sm text-slate-600">
            <Shield className="w-4 h-4 inline mr-1" />
            <strong>Administrator access</strong> is by invitation only.{' '}
            <Link to="/request-admin" className="text-slate-900 font-medium hover:underline">
              Request admin access
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
